/* eslint-disable @typescript-eslint/unbound-method */

import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EnvironmentToken } from '../../util';
import { ActivatedRoute, Router } from '@angular/router';
import { JobPreferencesApiService } from './job-preferences-api-service';
import { Environment, JobPreferencesDataRequest, ResumeDetail, ResumeLink, TalentMoatsAvailability } from '../../typings';
import { ApiService } from '../api.service';
import { TranslateService } from '@ngx-translate/core';
import { requireCheckboxesValidator } from '../eeoc/require-checkboxes.validator';
import { Option } from '../web-components/dropdown-box/dropdown-box.component';
import { JobPreferencesService } from './service/job-preferences.service';
import { EeocSources } from '../enums';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil } from 'rxjs/operators';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
    selector: 'app-job-preferences',
    templateUrl: './job-preferences.component.html',
    styleUrls: ['./job-preferences.component.scss'],
    standalone: false
})
export class JobPreferencesComponent implements OnInit, AfterViewInit {
  @ViewChild('locationSearch') locationSearchElementRef: ElementRef;
  @ViewChild('locationAutocompleteTrigger', { read: MatAutocompleteTrigger }) autocompleteTrigger: MatAutocompleteTrigger;

  talentId: number;
  uuid: string;
  submitted = false;
  formGroup: UntypedFormGroup;
  isApplication: boolean;
  isRegistration: boolean;
  showEeocForm: boolean;
  eeocFormUUID: string;
  countryId: number;
  lastUpdatedDate: string;
  fromRedeployEmail?: string;
  redeploySubmitted = false;
  pageTitle: string;
  currentResumeUploadDate = '';
  currentResumeUrl = '';
  currentResumeFileName: string;
  moatsLocation: { [k: string]: any } = { homeLocation: '', latitude: 0, longitude: 0 };
  availabilityTypeOptions: Option[] = [
    new Option(1, 'Available now'),
    new Option(2, 'Available with notice'),
    new Option(3, 'Available on a specific date')
  ];
  availabilityTypeSelected: number;
  availableWithNotice = false;
  availableOnDate = false;

  dataLoaded: Promise<boolean>;
  locationSuggestions: google.maps.places.AutocompleteSuggestion[] = [];
  isLoadingLocation = false;
  private destroy$ = new Subject<void>();
  private sessionToken: google.maps.places.AutocompleteSessionToken;
  private isGoogleMapsLoaded = false;
  geoLocation: GeoLocation;
  locationValid: boolean = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private apiService: ApiService,
    private jobPrefsApi: JobPreferencesApiService,
    private datePipe: DatePipe,
    private translator: TranslateService,
    private jobPreferencesService: JobPreferencesService,
    protected router: Router,
    private cdr: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    @Inject(EnvironmentToken) private environment: Environment
  ) {
    const uuid = this.route.snapshot.paramMap.get('uuid');
    this.fromRedeployEmail = this.route.snapshot.paramMap.get('redeploy');

    this.uuid = uuid;
    this.isApplication = this.jobPreferencesService.isApplication || this.route.snapshot.queryParamMap.get('isApplication') === 'true';
    this.isRegistration = this.jobPreferencesService.isRegistration || this.route.snapshot.queryParamMap.get('isRegistration') === 'true';
    this.countryId =
      this.jobPreferencesService.countryId === -1
        ? parseInt(this.route.snapshot.queryParamMap.get('talentCountryId'))
        : this.jobPreferencesService.countryId;
    this.showEeocForm = this.jobPreferencesService.showEeocForm || this.route.snapshot.queryParamMap.get('showEeocForm') === 'true';
    this.pageTitle = translator.instant('web-style-top-bar.preferences-edit');

    // If we actually have an uuid parameter, do this
    if (uuid !== null) {
      if (!this.fromRedeployEmail) {
        this.jobPrefsApi.getPersonIdByUuid(uuid).subscribe(personId => {
          if (personId > 0) {
            this.talentId = personId;
            this.getLastUpdatedTime();
          } else {
            top.location.replace(this.environment.matHost);
          }
        });
      } else {
        this.jobPrefsApi.getPersonInfoByUuidRedeployme(uuid).subscribe(data => {
          this.talentId = data.personId;
          this.showEeocForm = data.showEeocForm;
          this.eeocFormUUID = data.eeocFormUUId;
          this.populateJobPrefs();
        });
      }
    }
    // otherwise, pull user info based on authentication
    else {
      this.apiService.getUserInfo(false).subscribe(
        person => {
          this.talentId = person.id;

          this.populateJobPrefs();
        },
        error => {
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          console.error(`Error retrieving person by uuid: ${error.message}`);
        }
      );
    }
  }

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      hourlyMinimum: new UntypedFormControl(''),
      hourlyDesired: new UntypedFormControl(''),
      salaryMinimum: new UntypedFormControl(''),
      salaryDesired: new UntypedFormControl(''),
      phone: new UntypedFormControl(''),
      location: new UntypedFormControl(''),
      permissionToSubmit: new UntypedFormControl(''),
      resumeUpload: new UntypedFormControl(),
      submitAuth: new UntypedFormControl(''),
      availabilityTypeGroup: new UntypedFormGroup({
        availabilityType: new UntypedFormControl(''),
        numWeeksNoticeNeeded: new UntypedFormControl(''),
        availabilityDate: new UntypedFormControl('')
      }),
      employmentType: new UntypedFormGroup(
        {
          temp: new UntypedFormControl(false),
          tempToPerm: new UntypedFormControl(false),
          perm: new UntypedFormControl(false)
        },
        requireCheckboxesValidator()
      ),
      hours: new UntypedFormGroup(
        {
          partTime: new UntypedFormControl(false),
          fullTime: new UntypedFormControl(false)
        },
        requireCheckboxesValidator()
      ),
      workplace: new UntypedFormGroup(
        {
          onSite: new UntypedFormControl(false),
          remote: new UntypedFormControl(false)
        },
        requireCheckboxesValidator()
      )
    });

    this.formControls.employmentType.valueChanges.subscribe(val => {
      if (val.perm || val.tempToPerm) {
        this.formControls.salaryMinimum.setValidators([Validators.required]);
        this.formControls.salaryMinimum.updateValueAndValidity();
        this.formControls.salaryDesired.setValidators([Validators.required]);
        this.formControls.salaryDesired.updateValueAndValidity();
      } else {
        this.formControls.salaryMinimum.clearValidators();
        this.formControls.salaryMinimum.updateValueAndValidity();
        this.formControls.salaryDesired.clearValidators();
        this.formControls.salaryDesired.updateValueAndValidity();
      }
      if (val.temp || val.tempToPerm) {
        this.formControls.hourlyMinimum.setValidators([Validators.required]);
        this.formControls.hourlyMinimum.updateValueAndValidity();
        this.formControls.hourlyDesired.setValidators([Validators.required]);
        this.formControls.hourlyDesired.updateValueAndValidity();
      } else {
        this.formControls.hourlyMinimum.clearValidators();
        this.formControls.hourlyMinimum.updateValueAndValidity();
        this.formControls.hourlyDesired.clearValidators();
        this.formControls.hourlyDesired.updateValueAndValidity();
      }
    });
    this.initializeGooglePlacesAutocomplete();

    if (this.fromRedeployEmail) {
      // this.formGroup.get('location').valueChanges.subscribe(() => {
      //   const input = document.getElementById('locationAutocomplete') as HTMLInputElement;
      //   const autocomplete = new google.maps.places.Autocomplete(input);
      //
      //   autocomplete.addListener('place_changed', () => {
      //     const place = autocomplete.getPlace();
      //     if (!place.geometry) {
      //       return;
      //     }
      //     this.moatsLocation.homeLocation = place.formatted_address;
      //     this.moatsLocation.latitude = place.geometry.location.lat();
      //     this.moatsLocation.longitude = place.geometry.location.lng();
      //   });
      // });
    }
  }

  ngAfterViewInit() {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    hj('event', 'job_preferences_form_view');

    if (this.fromRedeployEmail === 'resumePreview') {
      const resumeSection = document.getElementById('redeployJobPreferencesResumeSection');
      resumeSection.scrollIntoView();
    }
  }

  populateJobPrefs(): void {
    if (this.jobPreferencesService.jobPreferencesData.talentId === -1) {
      this.jobPrefsApi.getCurrentJobPrefsData(this.talentId).subscribe(
        data => {
          this.jobPreferencesService.jobPreferencesData = data;
          this.patchFormGroups();
          this.getLastUpdatedTime();
        },
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        error => console.log('Failed to read job preferences form for person id: ' + this.talentId + '. Error: ' + error),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        () => (this.dataLoaded = Promise.resolve(true))
      );
    } else {
      this.getLastUpdatedTime();
      this.patchFormGroups();
      this.dataLoaded = Promise.resolve(true);
    }
  }

  patchFormGroups() {
    this.formGroup.get('hourlyMinimum').patchValue(this.jobPreferencesService.jobPreferencesData.money.minimumHourlyRate);
    this.formGroup.get('hourlyDesired').patchValue(this.jobPreferencesService.jobPreferencesData.money.desiredHourlyRate);
    this.formGroup.get('salaryMinimum').patchValue(this.jobPreferencesService.jobPreferencesData.money.minimumYearlySalary);
    this.formGroup.get('salaryDesired').patchValue(this.jobPreferencesService.jobPreferencesData.money.desiredYearlySalary);
    this.formGroup
      .get('employmentType')
      .get('temp')
      .patchValue(this.jobPreferencesService.jobPreferencesData.opportunity.contractSelected);
    this.formGroup
      .get('employmentType')
      .get('tempToPerm')
      .patchValue(this.jobPreferencesService.jobPreferencesData.opportunity.contractToHireSelected);
    this.formGroup
      .get('employmentType')
      .get('perm')
      .patchValue(this.jobPreferencesService.jobPreferencesData.opportunity.permanentSelected);
    this.formGroup
      .get('hours')
      .get('partTime')
      .patchValue(this.jobPreferencesService.jobPreferencesData.opportunity.partTimeSelected);
    this.formGroup
      .get('hours')
      .get('fullTime')
      .patchValue(this.jobPreferencesService.jobPreferencesData.opportunity.fullTimeSelected);
    this.formGroup
      .get('workplace')
      .get('onSite')
      .patchValue(this.jobPreferencesService.jobPreferencesData.opportunity.onSiteSelected);
    this.formGroup
      .get('workplace')
      .get('remote')
      .patchValue(this.jobPreferencesService.jobPreferencesData.opportunity.offSiteSelected);
    this.formGroup.get('phone').patchValue(this.jobPreferencesService.jobPreferencesData.jobPreferences.phone);
    this.formGroup.get('location').patchValue(this.jobPreferencesService.jobPreferencesData.travel.homeLocation);
    this.moatsLocation = {
      homeLocation: this.jobPreferencesService.jobPreferencesData.travel.homeLocation,
      latitude: this.jobPreferencesService.jobPreferencesData.travel.homeLocationLatitude,
      longitude: this.jobPreferencesService.jobPreferencesData.travel.homeLocationLongitude
    };
    this.availabilityTypeSelected = this.jobPreferencesService.jobPreferencesData.availability.availabilityType;
    if (this.availabilityTypeSelected == 1 || this.availabilityTypeSelected == 2 || this.availabilityTypeSelected == 3) {
      this.formGroup
        .get('availabilityTypeGroup')
        .get('availabilityType')
        .patchValue(this.jobPreferencesService.jobPreferencesData.availability.availabilityType);
      if (
        this.jobPreferencesService.jobPreferencesData.availability.availabilityType == 2 &&
        this.jobPreferencesService.jobPreferencesData.availability.numWeeksNoticeNeeded != null
      ) {
        this.availableWithNotice = true;
        this.formGroup
          .get('availabilityTypeGroup')
          .get('numWeeksNoticeNeeded')
          .patchValue(this.jobPreferencesService.jobPreferencesData.availability.numWeeksNoticeNeeded);
      }
      if (
        this.jobPreferencesService.jobPreferencesData.availability.availabilityType == 3 &&
        this.jobPreferencesService.jobPreferencesData.availability.availabilityDate != null
      ) {
        this.availableOnDate = true;
        this.formGroup
          .get('availabilityTypeGroup')
          .get('availabilityDate')
          .patchValue(this.jobPreferencesService.jobPreferencesData.availability.availabilityDate);
      }
    }
    this.formGroup.get('permissionToSubmit').patchValue(this.jobPreferencesService.jobPreferencesData.availability.preAuthorizedSubmittal);
    this.currentResumeUrl = this.jobPreferencesService.jobPreferencesData.jobPreferences.resumeLink?.displayLink;
    this.currentResumeUploadDate = this.jobPreferencesService.jobPreferencesData.jobPreferences.resume?.lastUpdatedDate;
    this.currentResumeFileName = this.jobPreferencesService.jobPreferencesData.jobPreferences.resume?.fileName;
  }

  submitMoats() {
    this.submitted = true;

    if (this.formGroup.valid) {
      const resumeLink: ResumeLink = { redirectLink: '', displayLink: '' };
      const previousResume: ResumeDetail = { fileName: '', lastUpdatedDate: null };
      const availability = this.parseAvailability();
      const hasPrimaryPhone = this.jobPreferencesService.jobPreferencesData.jobPreferences.hasPrimaryPhone;
      const request: JobPreferencesDataRequest = {
        talentId: this.talentId,
        formId: this.route.snapshot.paramMap.get('uuid'),
        fromRedeployEmail: !!this.fromRedeployEmail,
        jobPreferences: {
          talentId: this.talentId,
          updateSource: 8,
          updatingPersonId: this.talentId,
          phone: this.formGroup.get('phone').value,
          hasPrimaryPhone,
          resume: previousResume,
          resumeLink
        },
        money: {
          minimumHourlyRate: this.formGroup.get('hourlyMinimum').value,
          desiredHourlyRate: this.formGroup.get('hourlyDesired').value,
          minimumYearlySalary: this.formGroup.get('salaryMinimum').value,
          desiredYearlySalary: this.formGroup.get('salaryDesired').value
        },
        opportunity: {
          contractSelected: this.formGroup.get('employmentType').get('temp').value,
          contractToHireSelected: this.formGroup.get('employmentType').get('tempToPerm').value,
          permanentSelected: this.formGroup.get('employmentType').get('perm').value,
          partTimeSelected: this.formGroup.get('hours').get('partTime').value,
          fullTimeSelected: this.formGroup.get('hours').get('fullTime').value,
          onSiteSelected: this.formGroup.get('workplace').get('onSite').value,
          offSiteSelected: this.formGroup.get('workplace').get('remote').value,
          hybridSelected: false
        },
        availability: availability,
        travel: {
          personId: this.talentId,
          homeLocation: this.moatsLocation.homeLocation,
          homeLocationLatitude: this.moatsLocation.latitude,
          homeLocationLongitude: this.moatsLocation.longitude,
          modifyingPersonId: this.talentId
        }
      };
      /* eslint-disable @typescript-eslint/no-unsafe-argument*/
      const apiCall = this.formGroup.get('resumeUpload').value
        ? this.jobPrefsApi.submitMoatsWithResume(request, this.formGroup.get('resumeUpload').value)
        : this.jobPrefsApi.submitMoats(request);
      /* eslint-enable @typescript-eslint/no-unsafe-argument*/

      apiCall.subscribe({
        next: () => {
          let path = `myaquent/registration/`;
          if (!this.fromRedeployEmail) {
            if (this.showEeocForm) {
              path += `eeoc-page?talentCountryId=${this.countryId}&isApplication=${String(this.isApplication)}&isRegistration=${String(
                this.isRegistration
              )}`;
            } else {
              path += `talent/registration/success?isApplication=${String(this.isApplication)}&isRegistration=${String(this.isRegistration)}`;
            }
            top.location.replace(`${this.environment.matHost}/${path}`);
          } else {
            if (this.showEeocForm) {
              this.router.navigate(['eeoc-form', this.eeocFormUUID], { queryParams: { source: EeocSources.REDEPLOYME } });
            } else {
              this.router.navigate(['preferences-confirmed', this.uuid], { queryParams: { updatePreferences: false } } );
            }
          }
        },
        error: error => {
          if (error.status === 415) {
            this.snackBar.open('Unsupported file type. Please upload a PDF, DOCX, TXT, RTF, HTML, PNG, or JPEG file.', 'Close', {
              duration: 5000,
              panelClass: ['error-snackbar']
            });
          } else {
            this.snackBar.open('An error occurred while uploading your resume. Please try again.', 'Close', {
              duration: 5000,
              panelClass: ['error-snackbar']
            });
          }

          this.submitted = false;
        }
      });
    }
  }

  // TODO format international phone numbers

  formatPhoneNo(field) {
    const phoneNumDigits = field.value.replace(/\D/g, '');

    let formattedNumber = phoneNumDigits;
    if (phoneNumDigits.length >= 6)
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      formattedNumber = '(' + phoneNumDigits.substring(0, 3) + ') ' + phoneNumDigits.substring(3, 6) + '-' + phoneNumDigits.substring(6);
    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    else if (phoneNumDigits.length >= 3) formattedNumber = '(' + phoneNumDigits.substring(0, 3) + ') ' + phoneNumDigits.substring(3);

    field.value = formattedNumber;
  }

  selectAvailabilityType(event: any) {
    this.availabilityTypeSelected = event;

    switch (this.availabilityTypeSelected) {
      case 1:
        this.availableWithNotice = false;
        this.availableOnDate = false;
        break;
      case 2:
        this.availableWithNotice = true;
        this.availableOnDate = false;
        break;
      case 3:
        this.availableOnDate = true;
        this.availableWithNotice = false;
        break;
    }
  }

  private parseAvailability(): TalentMoatsAvailability {
    const availabilityType = this.availabilityTypeSelected;
    const preAuthorizedSubmittal = this.formGroup.get('permissionToSubmit').value;

    let availabilityDate;
    let numWeeksNoticeNeeded;

    if (availabilityType === 2) {
      numWeeksNoticeNeeded = this.formGroup.get('availabilityTypeGroup').get('numWeeksNoticeNeeded').value;
    } else if (availabilityType === 3) {
      availabilityDate = this.formGroup.get('availabilityTypeGroup').get('availabilityDate').value;
    }

    return {
      availabilityType,
      availabilityDate,
      numWeeksNoticeNeeded,
      preAuthorizedSubmittal
    };
  }

  setPermissionToSubmit(event: any) {
    this.formGroup.get('permissionToSubmit').setValue(event);
  }

  onFileSelected(event: any) {
    if (!event.target.files || event.target.files['length'] === 0) {
      return;
    }
    this.formGroup.get('resumeUpload').setValue(event.target.files[0]);
  }

  cancel() {
    this.redeploySubmitted = false;
    this.availableWithNotice = false;
    this.availableOnDate = false;
    this.formGroup.get('resumeUpload').reset();
    this.populateJobPrefs();
  }

  get formControls() {
    return this.formGroup.controls;
  }

  checkBoxGroupInvalid(formControl) {
    return this.submitted && !formControl.valid && !formControl.errors.requireCheckboxesValidator;
  }

  getLastUpdatedTime() {
    if (!this.isRegistration) {
      this.jobPrefsApi.getLastUpdatedTime(this.talentId).subscribe(date => {
        const lastUpdated = new Date(date);
        this.lastUpdatedDate = lastUpdated.toLocaleString('en-us', { month: 'long', year: 'numeric', day: 'numeric' });
      });
    }
  }

  private initializeGooglePlacesAutocomplete(): void {
    // Check if Google Maps is already loaded
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      this.isGoogleMapsLoaded = true;
      this.setupAutocomplete();
    } else {
      // Wait for Google Maps to load
      this.waitForGoogleMaps();
    }
  }

  /**
   * Wait for Google Maps API to be available.
   * This is necessary because the script might still be loading when our component initializes.
   */
  private waitForGoogleMaps(): void {
    const checkGoogleMaps = setInterval(() => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        clearInterval(checkGoogleMaps);
        this.isGoogleMapsLoaded = true;
        this.setupAutocomplete();
      }
    }, 100);

    // Timeout after 10 seconds to prevent infinite checking
    setTimeout(() => {
      clearInterval(checkGoogleMaps);
      if (!this.isGoogleMapsLoaded) {
        console.error('Google Maps failed to load');
      }
    }, 10000);
  }

  /**
   * Set up the autocomplete functionality once Google Maps is loaded.
   * This creates the session token and sets up the reactive search pipeline.
   */
  private setupAutocomplete(): void {
    // Create a new session token for billing efficiency
    this.createNewSessionToken();

    // Set up the reactive pipeline for handling user input
    this.setupAutocompletePipeline();
  }

  /**
   * Create a new session token for Google Maps billing.
   * Each session groups multiple autocomplete requests into one billable unit.
   */
  private createNewSessionToken(): void {
    this.sessionToken = new google.maps.places.AutocompleteSessionToken();
  }

  /**
   * Set up the reactive pipeline for autocomplete search.
   * This handles debouncing, filtering, and API calls.
   */
  private setupAutocompletePipeline(): void {
    this.formGroup.get('location').valueChanges
      .pipe(
        takeUntil(this.destroy$),
        // Wait 600ms after user stops typing
        debounceTime(600),
        // Only emit if the value actually changed
        distinctUntilChanged(),
        // Filter out short values and handle clearing
        filter(value => {
          if (!value || value.length < 4) {
            this.locationSuggestions = [];
            this.isLoadingLocation = false;
            this.locationValid = false;
            this.geoLocation = null;
            return false;
          }
          return true;
        }),
        // Cancel previous requests and make new API call
        switchMap(value => {
          this.isLoadingLocation = true;
          return this.searchPlaces(value);
        })
      )
      .subscribe({
        next: suggestions => {
          this.locationSuggestions = suggestions;
          this.isLoadingLocation = false;
        },
        error: error => {
          console.error('Autocomplete error:', error);
          this.locationSuggestions = [];
          this.isLoadingLocation = false;
        }
      });
  }

  /**
   * Search for place suggestions using the new Google Maps API.
   * This uses AutocompleteSuggestion.fetchAutocompleteSuggestions() instead of the old Autocomplete widget.
   */
  private async searchPlaces(query: string): Promise<google.maps.places.AutocompleteSuggestion[]> {
    try {
      const request = {
        input: query,
        sessionToken: this.sessionToken,
        // Include country restrictions if specified !!!!!! NO we want to allow for all countries
        // includedRegionCodes: this.allowedCountries.map(c => c.toUpperCase())
      };

      // Use the new static method from Google's API
      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      return suggestions || [];
    } catch (error) {
      console.error('Failed to fetch autocomplete suggestions:', error);
      return [];
    }
  }

  /**
   * Handle place selection from the autocomplete dropdown.
   * This fetches detailed place information and updates the form.
   */
  async onPlaceSelected(event: MatAutocompleteSelectedEvent): Promise<void> {
    const suggestion: google.maps.places.AutocompleteSuggestion = event.option.value;

    if (!suggestion?.placePrediction?.placeId) {
      return;
    }

    try {
      // Create a new Place instance to fetch detailed information
      const place = new google.maps.places.Place({
        id: suggestion.placePrediction.placeId
      });

      // Fetch only the fields we need
      await place.fetchFields({
        fields: ['id', 'displayName', 'formattedAddress', 'location']
      });

      // Get the place data
      const placeData = place.toJSON() as any;

      if (placeData && placeData.id) {
        // Extract location coordinates
        this.moatsLocation = {
          latitude: placeData.location?.lat || 0,
          longitude: placeData.location?.lng || 0
        };

        // Set the formatted address
        this.moatsLocation.homeLocation = placeData.formattedAddress || placeData.displayName || '';

        // Update the form control but do NOT emit a new valueChanges event
        this.formGroup.controls['location']
          .setValue(this.moatsLocation.homeLocation, { emitEvent: false });

        // Mark the field dirty / touched so validators behave
        this.formGroup.controls['location'].markAsDirty();
        this.formGroup.controls['location'].markAsTouched();

        // (Optional) close the panel so it doesn’t reopen
        this.autocompleteTrigger.closePanel();

        // Mark as valid and touched
        this.formGroup.controls['location'].setErrors(null);

        // Update talent tracking
        this.locationValid = true;

        // Clear suggestions
        this.locationSuggestions = [];

        // Trigger change detection
        this.cdr.detectChanges();

        // Create a new session token for the next search
        this.createNewSessionToken();
      }
    } catch (error) {
      console.error('Failed to get place details:', error);
      this.formGroup.controls['location'].setErrors({ placeDetailsError: true });
    }
  }

  /**
   * Converts whatever is in the control (an AutocompleteSuggestion object
   * right after a click, or the final address string after we patch it)
   * into the text that appears in the input.
   */
  displaySuggestion(
    value: google.maps.places.AutocompleteSuggestion | string | null
  ): string {
    if (!value) {
      return '';
    }

    // After we patch the control with the plain string, just echo it.
    if (typeof value === 'string') {
      return value;
    }

    // While the control still holds the Suggestion object, show its main text.
    return value.placePrediction?.text?.text ?? '';
  }

  /**
   * Get the main text from a suggestion for display.
   */
  getSuggestionMainText(suggestion: google.maps.places.AutocompleteSuggestion): string {
    return suggestion?.placePrediction?.text?.text || '';
  }

  /**
   * Get the secondary text (like city, state, country) from a suggestion.
   */
  getSuggestionSecondaryText(suggestion: google.maps.places.AutocompleteSuggestion): string {
    const fullText = suggestion?.placePrediction?.text?.text || '';
    const commaIndex = fullText.indexOf(',');
    return commaIndex > -1 ? fullText.substring(commaIndex + 1).trim() : '';
  }
  onHomeLocationBlur(): void {
    const control = this.formGroup.get('location');
    control?.markAsTouched();
    this.formGroup.get('location').patchValue(this.moatsLocation.homeLocation);

  }
}
